import { mdiDelete, mdiPause, mdiPlay, mdiSend } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useEffect } from 'react';

interface VoiceRecorderProps {
	isRecording: boolean;
	isPreviewingRecording: boolean;
	recordingDuration: number;
	recordingPaused: boolean;
	mediaBlobUrl?: string;
	audioRef: React.RefObject<HTMLAudioElement>;
	formatRecordingTime: (seconds: number) => string;
	onStartRecording: () => void;
	onStopRecording: () => void;
	onCancelRecording: () => void;
	onPreviewRecording: () => void;
	onSendVoiceMessage: () => void;
	disabled?: boolean;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
	isRecording,
	isPreviewingRecording,
	recordingDuration,
	recordingPaused,
	mediaBlobUrl,
	audioRef,
	formatRecordingTime,
	onStartRecording,
	onStopRecording,
	onCancelRecording,
	onPreviewRecording,
	onSendVoiceMessage,
	disabled = false,
}) => {
	// Add pulse animation CSS
	useEffect(() => {
		const style = document.createElement('style');
		style.id = 'voice-recorder-styles';
		style.textContent = `
			@keyframes pulse {
				0% {
					box-shadow: 0 0 0 0 rgba(239, 83, 80, 0.4);
				}
				70% {
					box-shadow: 0 0 0 10px rgba(239, 83, 80, 0);
				}
				100% {
					box-shadow: 0 0 0 0 rgba(239, 83, 80, 0);
				}
			}
		`;

		// Remove existing style if it exists
		const existingStyle = document.getElementById('voice-recorder-styles');
		if (existingStyle) {
			existingStyle.remove();
		}

		document.head.append(style);

		return () => {
			style.remove();
		};
	}, []);

	// Voice Recording UI - WhatsApp style
	if (isRecording) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					backgroundColor: '#f0f2f5',
					borderRadius: '24px',
					p: 1,
					mb: 1,
					gap: 2,
				}}
			>
				{/* Recording indicator */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						flex: 1,
					}}
				>
					<Box
						sx={{
							width: 12,
							height: 12,
							borderRadius: '50%',
							backgroundColor: 'error.main',
							animation: recordingPaused ? 'none' : 'pulse 1.5s infinite',
							mr: 2,
						}}
					/>
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
						<Typography variant="body2" color="text.secondary">
							{recordingPaused ? 'Paused' : 'Recording'}
						</Typography>
						<Typography
							variant="h6"
							color="error.main"
							sx={{ fontWeight: 'bold', fontSize: '1rem' }}
						>
							{formatRecordingTime(recordingDuration)}
						</Typography>
					</Box>
				</Box>

				{/* Recording controls */}
				<Box sx={{ display: 'flex', gap: 1 }}>
					{/* Cancel button */}
					<IconButton size="small" onClick={onCancelRecording} sx={{ color: 'error.main' }}>
						<Icon path={mdiDelete} size={0.8} />
					</IconButton>

					{/* Send button */}
					<IconButton size="small" color="primary" onClick={onStopRecording}>
						<Icon path={mdiSend} size={0.8} />
					</IconButton>
				</Box>
			</Box>
		);
	}

	// Voice Message Preview UI
	if (isPreviewingRecording && mediaBlobUrl) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					backgroundColor: '#f0f2f5',
					borderRadius: '24px',
					p: 1,
					mb: 1,
					gap: 2,
				}}
			>
				<audio ref={audioRef} src={mediaBlobUrl} style={{ display: 'none' }} />

				{/* Voice message preview */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						flex: 1,
					}}
				>
					<IconButton size="small" onClick={onPreviewRecording} sx={{ mr: 1 }}>
						<Icon path={audioRef.current?.paused !== false ? mdiPlay : mdiPause} size={0.8} />
					</IconButton>

					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
						<Typography variant="body2" color="text.secondary">
							Voice message
						</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
							Duration: {formatRecordingTime(recordingDuration)}
						</Typography>
					</Box>
				</Box>

				{/* Preview controls */}
				<Box sx={{ display: 'flex', gap: 1 }}>
					{/* Cancel button */}
					<IconButton size="small" onClick={onCancelRecording} sx={{ color: 'error.main' }}>
						<Icon path={mdiDelete} size={0.8} />
					</IconButton>

					{/* Send button */}
					<IconButton size="small" color="primary" onClick={onSendVoiceMessage}>
						<Icon path={mdiSend} size={0.8} />
					</IconButton>
				</Box>
			</Box>
		);
	}

	// Return null when not recording or previewing
	return null;
};
